import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import {
  EmailValidateRequest,
  ForgotPasswordConfirmRequest,
  ForgotPasswordRequest,
  OtpUpdateRequest,
  SecretGenerateResponse,
  UserChangeEmailRequest,
} from '@primatoo-ms-admin';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';

import { AgGridLangMap } from '@pt/pt-models';
import { DataProtectionGovernanceService } from './interface/data-protection-governance-service';
import { CheckEmailService } from './interface/check-email-service';
import { TranslateService } from '@ngx-translate/core';
import { User, UserCreateRequest, UserUpdateRequest } from '@ms-admin-entities';
import { CreateResponse } from 'it/primatoo/openapi/api/common/schemas/CreateResponse';
import { ApiClientConfig } from './api-client-config';
import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';
import {
  PasswordParamsResponse,
  PriorityPatchRequest,
} from '@primatoo-ms-organization';

@Injectable({
  providedIn: 'root',
})
export class AdmUsersService
  extends DataProtectionGovernanceService
  implements CheckEmailService
{
  private readonly endpoint: string;
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient,
    private translateService: TranslateService
  ) {
    super(httpClient);
    this.endpoint = this.config.endPoint + '/api/adm/users/';
    this.endpointV2 = this.config.endPoint + '/api/adm/v2/users/';
  }

  public checkEmail(
    value: string,
    currentUserEmail: string = null
  ): Observable<void> {
    const url = this.endpoint + `email-validate`;

    if (currentUserEmail !== value) {
      return this.httpClient.post<void>(url, { email: value });
    } else {
      return EMPTY;
    }
  }

  /**
   * In adm non è presente l'api
   */
  findUserColumns(): Observable<ColumnWithLocalization[]> {
    const agGridLangMap = new AgGridLangMap(this.translateService.currentLang);
    return of([
      {
        type: ColumnType.TEXT,
        column: 'id',
        label: agGridLangMap.getOf('id'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'username',
        label: agGridLangMap.getOf('username'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'email',
        label: agGridLangMap.getOf('email'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'name',
        label: agGridLangMap.getOf('name'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.TEXT,
        column: 'surname',
        label: agGridLangMap.getOf('surname'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.BOOLEAN,
        column: 'banned',
        label: agGridLangMap.getOf('banned'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.DATE,
        column: 'lastLoggedAt',
        size: 300,
        label: agGridLangMap.getOf('lastLoggedAt'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
      {
        type: ColumnType.DATE,
        column: 'createdAt',
        size: 300,
        label: agGridLangMap.getOf('createdAt'),
        uiAvailable: true,
        uiUserShow: true,
        operations: [],
      },
    ]);
  }

  getEndPointV1(): string {
    return this.endpoint;
  }

  getUsers(): Observable<User[]> {
    const url = this.endpoint + `enabled/`;
    return this.httpClient.get<User[]>(url);
  }

  getUserById(userId: number): Observable<User> {
    const url = this.endpoint + userId + '/';
    return this.httpClient.get<User>(url);
  }

  getSelfDetails(): Observable<User> {
    const url = this.endpoint + 'self/';
    return this.httpClient.get<User>(url);
  }

  updateUserSelf(userUpdateRequest: UserUpdateRequest): Observable<void> {
    const url = this.endpoint + 'self/';
    return this.httpClient.put<void>(url, userUpdateRequest);
  }

  createUser(userCreateRequest: UserCreateRequest): Observable<CreateResponse> {
    const url = this.endpoint;
    return this.httpClient.post<CreateResponse>(url, userCreateRequest);
  }

  updateUser(userId: number, request: UserUpdateRequest): Observable<void> {
    const url = this.endpoint + userId + '/';
    return this.httpClient.put<void>(url, request);
  }

  public checkUsername(value: string): Observable<any> {
    const url = this.endpoint + `username-validate`;
    return this.httpClient.post<any>(url, { username: value });
  }

  public emailValidate(
    emailValidateRequest: EmailValidateRequest
  ): Observable<any> {
    const url = this.endpoint + `email-validate`;
    return this.httpClient.post(url, emailValidateRequest);
  }

  public changeEmail(
    changeEmailRequest: UserChangeEmailRequest
  ): Observable<any> {
    const url = this.endpoint + `change-email`;
    return this.httpClient.post(url, changeEmailRequest);
  }

  public changeEmailConfirm(token: string): Observable<any> {
    const url = this.endpoint + `change-email/confirm/${token}/`;
    return this.httpClient.post(url, {});
  }

  public getPasswordHelpParams(): Observable<PasswordParamsResponse> {
    const url = this.endpoint + `pw-params`;
    return this.httpClient.get<PasswordParamsResponse>(url);
  }

  public passwordValidate(pw: string): Observable<any> {
    const url = this.endpoint + `pw-validate`;
    return this.httpClient.post<any>(url, { pw });
  }

  public changePassword(
    oldPassword: string,
    newPassword: string
  ): Observable<any> {
    const url = this.endpoint + `change-password`;
    return this.httpClient.post<any>(url, { oldPassword, newPassword });
  }

  public forgotPassword(
    domain: string,
    body: ForgotPasswordRequest,
    rcToken: string
  ): Observable<any> {
    let httpRequest;
    const url = this.endpoint + 'forgot-password';

    if (rcToken) {
      let header: HttpHeaders = new HttpHeaders();
      header = header.set('X-Recaptcha', rcToken);
      httpRequest = this.httpClient.post<any>(url, body, { headers: header });
    } else {
      httpRequest = this.httpClient.post<any>(url, body);
    }

    return httpRequest;
  }

  public forgotPasswordConfirm(
    domain: string,
    token: string,
    body: ForgotPasswordConfirmRequest,
    rcToken: string
  ): Observable<any> {
    let httpRequest;
    const url = this.endpoint + 'forgot-password/confirm/' + token + '/';

    if (rcToken) {
      let header: HttpHeaders = new HttpHeaders();
      header = header.set('X-Recaptcha', rcToken);
      httpRequest = this.httpClient.post<any>(url, body, { headers: header });
    } else {
      httpRequest = this.httpClient.post<any>(url, body);
    }

    return httpRequest;
  }

  public selfOtpGenerate(): Observable<SecretGenerateResponse> {
    const url = this.endpoint + `self/otp/`;
    return this.httpClient.get<SecretGenerateResponse>(url);
  }

  public selfOtpUpdate(secret: OtpUpdateRequest): Observable<any> {
    const url = this.endpoint + `self/otp/`;
    return this.httpClient.put<any>(url, secret);
  }

  public selfOtpReset(): Observable<any> {
    const url = this.endpoint + `self/otp/`;
    return this.httpClient.delete<any>(url);
  }

  public setPriority(
    id: number,
    request: PriorityPatchRequest
  ): Observable<any> {
    return undefined;
  }

  public userOtpReset(userId: number): Observable<void> {
    const url = this.endpoint + `${userId}/otp/`;
    return this.httpClient.delete<void>(url);
  }
}
